/**
 * Layout helpers for the continuous Bible reading map.
 *
 * Chapters flow left to right in one grid per testament with no break between
 * books: Genesis 50 is followed directly by Exodus 1. Each book gets an
 * alternating band tint and a stepped boundary line, and the gutter on the
 * left names whichever book owns the first column of a row.
 */

const BOOK_ABBREVIATIONS = {
  Genesis: 'Gen', Exodus: 'Exod', Leviticus: 'Lev', Numbers: 'Num', Deuteronomy: 'Deut',
  Joshua: 'Josh', Judges: 'Judg', Ruth: 'Ruth', '1 Samuel': '1 Sam', '2 Samuel': '2 Sam',
  '1 Kings': '1 Kgs', '2 Kings': '2 Kgs', '1 Chronicles': '1 Chr', '2 Chronicles': '2 Chr',
  Ezra: 'Ezra', Nehemiah: 'Neh', Esther: 'Esth', Job: 'Job', Psalm: 'Ps', Psalms: 'Ps',
  Proverbs: 'Prov', Ecclesiastes: 'Eccl', 'Song of Solomon': 'Song', Isaiah: 'Isa',
  Jeremiah: 'Jer', Lamentations: 'Lam', Ezekiel: 'Ezek', Daniel: 'Dan', Hosea: 'Hos',
  Joel: 'Joel', Amos: 'Amos', Obadiah: 'Obad', Jonah: 'Jonah', Micah: 'Mic', Nahum: 'Nah',
  Habakkuk: 'Hab', Zephaniah: 'Zeph', Haggai: 'Hag', Zechariah: 'Zech', Malachi: 'Mal',
  Matthew: 'Matt', Mark: 'Mark', Luke: 'Luke', John: 'John', Acts: 'Acts', Romans: 'Rom',
  '1 Corinthians': '1 Cor', '2 Corinthians': '2 Cor', Galatians: 'Gal', Ephesians: 'Eph',
  Philippians: 'Phil', Colossians: 'Col', '1 Thessalonians': '1 Thess',
  '2 Thessalonians': '2 Thess', '1 Timothy': '1 Tim', '2 Timothy': '2 Tim', Titus: 'Titus',
  Philemon: 'Phlm', Hebrews: 'Heb', James: 'Jas', '1 Peter': '1 Pet', '2 Peter': '2 Pet',
  '1 John': '1 John', '2 John': '2 John', '3 John': '3 John', Jude: 'Jude', Revelation: 'Rev'
};

/** Standard short form of a book name, or the name itself if unknown. */
export function abbreviateBook(bookName) {
  return BOOK_ABBREVIATIONS[bookName] || bookName;
}

/**
 * Chunk a biblically-ordered chapter list into grid rows of `columns` cells.
 *
 * Each row carries the book that owns its first column, whether that book's
 * name should be shown in the gutter (only on the first row it owns), and
 * whether a boundary line runs along the top of the gutter. Each cell carries
 * its band parity plus which edges need a boundary line: the top edge when
 * the cell above belongs to a different book, the left edge when the cell to
 * the left does. Together those edges trace the stepped outline of every
 * book without any absolute positioning.
 */
export function buildBibleMapRows(chapters, columns) {
  if (!chapters?.length || !(columns >= 1)) return [];

  const sequence = [];
  let bookIndex = -1;
  let lastBook = null;
  chapters.forEach((chapter) => {
    if (chapter.book !== lastBook) {
      bookIndex += 1;
      lastBook = chapter.book;
    }
    sequence.push({ chapter, bookIndex });
  });

  const rows = [];
  let previousOwner = -1;

  for (let start = 0; start < sequence.length; start += columns) {
    const end = Math.min(start + columns, sequence.length);
    const owner = sequence[start].bookIndex;
    const cells = [];

    for (let i = start; i < end; i += 1) {
      const { chapter, bookIndex: current } = sequence[i];
      const above = i - columns >= 0 ? sequence[i - columns] : null;
      cells.push({
        chapter,
        band: current % 2,
        boundaryTop: Boolean(above && above.bookIndex !== current),
        boundaryLeft: i > start && sequence[i - 1].bookIndex !== current
      });
    }

    rows.push({
      key: start,
      book: sequence[start].chapter.book,
      band: owner % 2,
      showLabel: owner !== previousOwner,
      boundaryTop: previousOwner !== -1 && previousOwner !== owner,
      cells
    });
    previousOwner = owner;
  }

  return rows;
}
